<template>
  <div class="model-viewer-container">
    <div ref="container" :class="isHide ? 'model-container op0' : 'model-container'" :style="{ top: top }"></div>
  </div>
</template>

<script setup>
import { eventBus } from "@/utils/eventBus"
import { ref, onMounted, onBeforeUnmount, defineProps } from 'vue'
import * as THREE from 'three'
import gsap from 'gsap'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls'
import { RGBELoader } from 'three/examples/jsm/loaders/RGBELoader.js'
import TWEEN from '@tweenjs/tween.js'


// 组件属性
const props = defineProps({
  url: {
    type: String,
    default: ''
  },
  top: {
    type: String,
    default: ''
  }
})
const isHide = ref(false)
// DOM引用和Three.js对象
const container = ref(null)
const loading = ref(false)
let scene, camera, renderer, controls, model, mixer, clock, texture, cover
// 存储灯光引用
let lights = []
// let totalSteps = 72;
let textureSteps = { u: 9, v: 8 };
let params = {
  texture: {
    index: 0,
    step: { u: 1 / textureSteps.u, v: 1 / textureSteps.v },
    alphaTest: 0
  }
}

// 销毁当前模型和相关资源
const destroyModel = () => {
  if (model) {
    // 移除模型及其所有子对象
    scene.remove(model);

    // 清理材质和几何体
    model.traverse((child) => {
      if (child.isMesh) {
        if (child.geometry) {
          child.geometry.dispose();
        }
        if (child.material) {
          if (Array.isArray(child.material)) {
            child.material.forEach(material => material.dispose());
          } else {
            child.material.dispose();
          }
        }
      }
    });

    model = null;
  }

  // 清理所有灯光
  destroyLights();

  // 清理动画混合器
  if (mixer) {
    mixer.stopAllAction();
    mixer = null;
  }

  // 重置控制器目标
  if (controls) {
    controls.target.set(0, 0, 0);
    controls.update();
  }

  // 重置相机位置
  if (camera) {
    camera.position.set(0, 0, 5);
  }

  console.log('模型和灯光已销毁');
}

// 销毁所有灯光
const destroyLights = () => {
  lights.forEach(light => {
    if (light && scene) {
      scene.remove(light);
      //灯光对象不需要dispose()，但需要从场景中移除
    }
  });
  lights = [];
  console.log('灯光已销毁');
}

// 初始化场景
const initScene = () => {
  // 如果场景已存在，先清理
  if (scene) {
    while (scene.children.length > 0) {
      scene.remove(scene.children[0]);
    }
  } else {
    // 创建新场景
    scene = new THREE.Scene()
  }

  // 确保容器已挂载
  if (!container.value) {
    console.error('Container not mounted yet')
    return
  }

  // 如果渲染器不存在，创建它
  if (!renderer) {
    // 创建渲染器
    renderer = new THREE.WebGLRenderer({
      antialias: true,
      alpha: true,
      logarithmicDepthBuffer: true
    })
    renderer.setSize(container.value.clientWidth, container.value.clientHeight);
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.outputColorSpace = THREE.SRGBColorSpace;
    //renderer.toneMappingExposure = 1;
    container.value.appendChild(renderer.domElement)
  }

  // 如果相机不存在，创建它
  if (!camera) {
    // 创建相机
    camera = new THREE.PerspectiveCamera(
      60,
      container.value.clientWidth / container.value.clientHeight,
      0.1,
      100
    )
    camera.position.z = 5;
  }

  // 如果控制器不存在，创建它
  if (!controls) {
    // 添加轨道控制器
    controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = false;
    controls.dampingFactor = 2
    controls.minDistance = 1
    controls.maxDistance = 3
    controls.enablePan = false;
  }

  // 创建时钟用于动画
  if (!clock) {
    clock = new THREE.Clock();
  }
  eventBus.on('message', handleMessage);
}

//重新扫描，卸掉模型
const handleMessage = () => {
  console.log('重新扫描，卸掉模型')
  destroyModel();
}

//重制摄像头
const resetPositionCamera = (model, controls, camera) => {
  const box = new THREE.Box3().setFromObject(model);
  const center = box.getCenter(new THREE.Vector3());
  const radius = box.getSize(new THREE.Vector3()).length();
  const position = center.clone();
  position.z = radius * 2;
  controls.minDistance = radius;
  controls.maxDistance = radius * 4;
  camera.position.copy(position);
};

// 创建基本灯光
const createBasicLights = () => {
  // 先清理现有灯光
  destroyLights();

  // 添加环境光
  // const ambientLight = new THREE.AmbientLight(0xffffff, 0.3);
  // scene.add(ambientLight);
  // lights.push(ambientLight);
  // console.log('基本灯光已创建');
}

// 创建模型特定灯光
const createModelSpecificLights = (url) => {


  if (url === 'hmjyhl' || url === 'hmjyqx') {
    const frontLight = new THREE.DirectionalLight(0xffffff, 1);
    frontLight.position.set(-0.5, 0, 1);
    scene.add(frontLight);
    lights.push(frontLight);

    const directionalLight = new THREE.DirectionalLight(0xffffff, 1)
    directionalLight.position.set(1, 0.5, 1)
    scene.add(directionalLight)
    lights.push(directionalLight);

    const hemisphereLight = new THREE.HemisphereLight(0xffffff, 0xffffff, 1);
    scene.add(hemisphereLight);
    lights.push(hemisphereLight);
  } else {
    // const frontLight = new THREE.DirectionalLight(0xffffff, 1.2);
    // frontLight.position.set(-0.5, 0, 1);
    // scene.add(frontLight);
    // lights.push(frontLight);
  }

  console.log('模型特定灯光已创建');
}

// 加载模型
const loadModel = async (modelUrl = null) => {
  // 先销毁现有模型和灯光
  destroyModel();
  loading.value = true;

  try {
    const loader = new GLTFLoader();
    let url = modelUrl || localStorage.getItem('model_url') || props.url;

    // 如果没有提供URL，使用默认值
    if (!url) {
      console.error('No model URL provided');
      loading.value = false;
      return;
    }
    // 创建基本灯光
    createBasicLights();
    //const AD_URL = process.env.VUE_APP_AD_URL;
    const gltf = await loader.loadAsync(
      //`https://magictest.oss-cn-shanghai.aliyuncs.com/model/${url}/model.glb`,
      //`/model/hmjyhl/model.glb`,
      `${process.env.VUE_APP_CDN_URL}/model/${url}/model.glb`,
    );

    model = gltf.scene;
    // 如果有动画，设置动画混合器
    // if (gltf.animations && gltf.animations.length) {
    //   mixer = new THREE.AnimationMixer(model);
    //   gltf.animations.forEach((clip) => {
    //     mixer.clipAction(clip).play();
    //   });
    // }

    // object.traverse((child) => { 
    //   if (child.isMesh) {
    //      child.material.emissiveMap = child.material.map;
    //      }
    //     })


    model.traverse((child) => {
      if (child.isMesh) {
        if (child.material && child.material.map) {
          child.material.map.anisotropy = 8;
          child.material.emissiveMap = child.material.map;
          child.material.emissive = child.material.color;

        }
        child.material.transparent = true;
        child.material.opacity = 0; // 初始完全透明
        if (scene.environment) {
          child.material.envMap = scene.environment; // 绑定环境映射
          child.material.envMapIntensity = 1; // 反射强度（1-2之间较自然）
          child.material.needsUpdate = true;
        }
        child.material.envMapIntensity = 1.5;
        if (child.material.emissive) {
          child.material.emissiveIntensity = 0.5;
          child.material.needsUpdate = true;
        }
      }
    });

    // 创建模型特定灯光
    createModelSpecificLights(url);

    scene.add(model);
    const model_name = localStorage.getItem('model_url') || props.url;
    if (model_name === 'ysc') {
      setTopDownView();
    }

    // 自动调整模型位置和相机
    fitModelToView();
    model.scale.set(0.1, 0.1, 0.1);
    console.log('模型加载完成');

    if (url !== 'ysc') {
      eventBus.emit('playAnimate', 'play');
    } else {
      eventBus.emit('model_start', 'play');
    }

  } catch (error) {
    console.log('模型加载失败');
    console.error('Error loading model:', error);
  } finally {
    loading.value = false;
  }
};

// 重新加载模型
const reloadModel = () => {
  const url = localStorage.getItem('model_url') || props.url;
  if (url) {
    loadModel(url);
  }
}

// 设置俯视图
const setTopDownView = () => {
  if (!model || !camera || !controls) return

  // 获取模型边界框和中心点
  const box = new THREE.Box3().setFromObject(model)
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())
  const model_name = localStorage.getItem('model_url') || props.url;

  let rotationRad = 0; //0度

  // 计算模型高度
  const height = size.y

  // 计算腑视角10度所需的相机位置
  let angle = 0; //0度
  let distance;

  if (model_name == 'ysc') {
    rotationRad = 30 * (Math.PI / 180); // 旋转角度转换为弧度
    controls.maxDistance = 1.2 // 最大缩放距离
    angle = 10 * (Math.PI / 180) // 10度转换为弧度
    distance = Math.max(size.x, size.y, size.z) * 3
  } else {
    angle = -12 * (Math.PI / 180) // 10度转换为弧度
    distance = Math.max(size.x, size.y, size.z) * 2
  }

  // 计算相机位置
  let cameraPosition = null
  if (model_name !== 'ysc') {
    cameraPosition = new THREE.Vector3(
      center.x,
      center.y + height / 2 + Math.sin(angle) * distance,
      center.z + Math.cos(angle) * distance
    )
  } else {
    cameraPosition = new THREE.Vector3(
      center.x + Math.sin(rotationRad) * Math.cos(angle) * distance,
      center.y + height / 2 + Math.sin(angle) * distance,
      center.z + Math.cos(rotationRad) * Math.cos(angle) * distance
    )
  }

  // 使用GSAP动画平滑过渡到腑视角
  gsap.to(camera.position, {
    x: cameraPosition.x,
    y: cameraPosition.y,
    z: cameraPosition.z,
    duration: 1.5,
    ease: "power2.out",
    onUpdate: () => {
      controls.target.copy(center)
      controls.update()
    }
  })
}

// 初始化环境
const initEnvironment = async () => {
  try {
    const texture = await new RGBELoader().loadAsync(`${process.env.VUE_APP_CDN_URL}/texture/hdr/dancing_hall_2k.hdr`)
    texture.mapping = THREE.EquirectangularReflectionMapping
    scene.environment = texture
    texture.dispose();
  } catch (error) {
    console.error('Failed to load HDR texture:', error)
  }

}

// 自动调整模型位置和相机
const fitModelToView = () => {
  if (!model) return

  const box = new THREE.Box3().setFromObject(model)
  const center = box.getCenter(new THREE.Vector3())
  const size = box.getSize(new THREE.Vector3())

  // 居中模型
  model.position.x += (model.position.x - center.x)
  model.position.y += (model.position.y - center.y)
  model.position.z += (model.position.z - center.z)

  // 调整相机位置
  const maxDim = Math.max(size.x, size.y, size.z)
  const fov = camera.fov * (Math.PI / 180)
  let cameraZ = Math.abs(maxDim / 1 * Math.tan(fov * 1))
  cameraZ *= 2.5 // 稍微拉远一点
  camera.position.z = cameraZ

  const minZ = box.min.z
  const cameraToFarEdge = (minZ < 0) ? -minZ + cameraZ : cameraZ - minZ

  camera.far = cameraToFarEdge * 2
  camera.updateProjectionMatrix()

  // 重置控制器
  if (controls) {
    controls.target.copy(center)
    controls.update()
  }
  resetPositionCamera(model, controls, camera);
}

// 窗口大小变化处理
const onWindowResize = () => {
  if (!camera || !container.value) return;
  camera.aspect = container.value.clientWidth / container.value.clientHeight
  camera.updateProjectionMatrix()
  renderer.setSize(container.value.clientWidth, container.value.clientHeight)
}

// 使用 GSAP 执行动画
const startAnimation = () => {
  // 缩放动画：由大变小
  gsap.to(model.scale, {
    x: 1,
    y: 1,
    z: 1,
    duration: 1.5,
    ease: "power3.out"
  })

  // 遍历所有材质，设置透明度动画
  model.traverse((child) => {
    if (child.isMesh) {
      gsap.to(child.material, {
        opacity: 1,
        duration: 1.5,
        ease: "sine.inOut"
      })
    }
  })
}

// 动画循环
const animate = () => {
  requestAnimationFrame(animate)
  const delta = clock.getDelta()
  if (mixer) {
    mixer.update(delta)
  }
  if (controls) {
    controls.update()
  }
  TWEEN.update()
  renderer.render(scene, camera)
}

// 组件挂载时初始化
onMounted(() => {
  initScene()
  initEnvironment()
  eventBus.on('model_found', () => {
    loadModel()
    animate()
  })



  eventBus.on('model_start', () => {
    startAnimation()
  })

  eventBus.on('model_hide', (value) => {
    console.log(value)
    isHide.value = value
  })




  // 监听重新加载模型的请求
  eventBus.on('reload_model', (url) => {
    loadModel(url);
  })
})

// 组件卸载时清理
onBeforeUnmount(() => {
  window.removeEventListener('resize', onWindowResize)
  if (container.value && renderer) {
    container.value.removeChild(renderer.domElement)
  }

  // 清理所有资源
  destroyModel();

  if (renderer) {
    renderer.dispose();
    renderer = null;
  }

  // 移除事件监听器
  eventBus.off('message', handleMessage);
  eventBus.off('model_found');
  eventBus.off('model_start');
  eventBus.off('reload_model');
})

// 暴露方法给父组件
defineExpose({
  destroyModel,
  loadModel
})
</script>


<style scoped lang="scss">
.model-viewer-container {
  position: relative;
  width: 100vw;
  height: 100vh;
  // background-color: rgba($color: #000000, $alpha: 0.3)
}

.model-container {
  width: 100vw;
  height: 90vh;
  position: absolute;
  z-index: 9;
}

.model-controls {
  width: 100vw;
  height: 50vh;
  position: absolute;
  top: 45vh;
  z-index: 9;

  .content {
    flex: 1;
    width: 100vw;
    display: flex;
    flex-direction: column;
    justify-content: flex-end;
    align-items: center;
    position: relative;
    bottom: 20px;
    left: 0;
    right: 0;
    z-index: 99;

    .center-tip {
      min-height: 40px;
      width: 80%;
      //outline: 1px solid deepskyblue;
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      user-select: none;
      pointer-events: none;
      gap: 1rem 0;

      img {
        padding-top: 40%;
        width: 100%;
        height: 100%;
      }

      span {
        text-shadow: 0 2px 3px rgba(0, 0, 0, 0.3);
        font-weight: 500;
        font-size: .8rem;
        color: #EDEDED;
      }
    }

    .bottom-tip {
      display: flex;
      justify-content: space-between;
      align-items: center;
      width: 80%;
      //outline: 1px solid black;
      position: fixed;
      bottom: 30%;
      left: 50%;
      transform: translateX(-50%);

      span {
        font-size: .8rem;
      }

      span:nth-of-type(1) {
        visibility: hidden;
      }

      span:nth-of-type(2) {
        pointer-events: auto;
      }

      img {
        vertical-align: -4px;
      }

    }
  }
}

.op0 {
  opacity: 0;
}

.btn_reset {
  pointer-events: auto;
}
</style>